import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { logoutUser } from "../../../slices/authSlice";
import { fetchData } from "../../../utils/functions"; // ✅ fix path
import {
  ThinTextField,
  BackButton,
  SubmitButton,
} from "../../common/GeneralComponent"; // ✅ fix path
import { Eye, EyeOff } from "lucide-react";

const validationSchema = Yup.object({
  oldPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(6, "Password must be at least 6 characters long")
    .max(15, "Password must be at most 15 characters long")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/\d/, "Password must contain at least one digit")
    .matches(
      /[@$!%*?&]/,
      "Password must contain at least one special character"
    ),
  confirmPassword: Yup.string()
    .required("confirm password is required")
    .oneOf([Yup.ref("newPassword"), null], "Password do NOT match"),
});

const ChangePassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const formik = useFormik({
    initialValues: { oldPassword: "", newPassword: "", confirmPassword: "" },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        await fetchData(
          "user/change-password",
          "PUT",
          {
            oldPassword: values.oldPassword,
            password: values.newPassword,
            confirmPassword: values.confirmPassword,
          },
          {},
          {},
          navigate,
          "/",
          dispatch
        );

        toast.success("Password updated successfully");

        // ✅ Call logout after password change
        await dispatch(logoutUser());
        navigate("/login", { replace: true });
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            error.message ||
            "Failed to update password."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Change Password
        </h1>
        <BackButton to="/" text="Back" />
      </div>
      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="text-[0.875rem] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Change Your Password
          </h1>
        </div>
        <form onSubmit={formik.handleSubmit} className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Old Password */}
            <div className="relative">
              <ThinTextField
                id="oldPassword"
                label="Current Password"
                type={showOld ? "text" : "password"}
                placeholder="Enter Current password"
                value={formik.values.oldPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.oldPassword && formik.errors.oldPassword}
                helperText={
                  formik.touched.oldPassword && formik.errors.oldPassword
                }
              />
              <button
                type="button"
                onClick={() => setShowOld(!showOld)}
                className="absolute right-3 top-[40px] text-gray-500"
              >
                {showOld ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* New Password */}
            <div className="relative">
              <ThinTextField
                id="newPassword"
                label="New Password"
                type={showNew ? "text" : "password"}
                placeholder="Enter new password"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.newPassword && formik.errors.newPassword}
                helperText={
                  formik.touched.newPassword && formik.errors.newPassword
                }
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-[40px] text-gray-500"
              >
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <ThinTextField
                id="confirmPassword"
                label="Confirm New Password"
                type={showConfirm ? "text" : "password"}
                placeholder="Re-enter new password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-[40px] text-gray-500"
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <SubmitButton
              type="submit"
              text="Update Password"
              isLoading={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword; // ✅ proper export
